import React, { useCallback, useEffect, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
// api data
import { useInsuranceProviderChoices } from "../../../queries/insuranceProviders";
// form
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  useIntakeFormStore,
  insuranceInfoSchema,
  BCBS_PROVIDER_LABEL_PREFIX,
  type TInsuranceInfoSchema,
} from "../intakeFormState";
// UI
import { IntakeFormScreenContainer } from "../IntakeFormScreenContainer";
import { Typography } from "../../../components/Typography/Typography";
import { TextInput } from "../../../components/TextInput/TextInput";
import { SelectInput, type SelectInputChoice } from "../../../components/SelectInput/SelectInput";
import { NextButton } from "../NextButton";
// controller (from layout)
import { useOutletContext } from "react-router-dom";
import type { IntakeOutletContext } from "../IntakeFormLayout";
import { useCreatePhiFileReferenceMut } from "../../../queries/phiFileReferences";
// utils
import { useBuildIntakePhiFileInputFn } from "../../../utils/intakeFormSubmitUtils";
import { trackEvent } from "../../../utils/daniel-analytics";
// assets
import insuranceCardImg from "../../../assets/insuranceCard.png";
import { BCBSIntakeFormInsuranceInfo } from "./BCBSIntakeFormInsuranceInfo";
// types
import { PhiFileReferenceClaimType } from "../../../__generated__/mcs_backend_v1/graphql";

export const IntakeFormInsuranceInfoScreen = React.memo(() => {
  const { insuranceInfo, handleInsuranceInfoSubmit } = useIntakeFormStore();
  const { t } = useTranslation();

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm<TInsuranceInfoSchema>({
    resolver: yupResolver(insuranceInfoSchema),
    reValidateMode: "onBlur",
    mode: "onBlur",
    defaultValues: insuranceInfo,
  });

  const outletContext: IntakeOutletContext = useOutletContext();
  const { onSubmit: afterSubmit } = outletContext;
  const [selectedInsuranceProviderSlug, setSelectedInsuranceProviderSlug] = useState<string | null>(
    insuranceInfo?.insuranceProviderId || null,
  );
  const [isChildPolicyHolder, setIsChildPolicyHolder] = useState<string | null>(null);
  const { choices: choicesFromApi } = useInsuranceProviderChoices();
  const [frontInsuranceImageFile, setFrontInsuranceImageFile] = useState<File | null>(null);
  const [backInsuranceImageFile, setBackInsuranceImageFile] = useState<File | null>(null);

  useEffect(() => {
    trackEvent("insurance-provider-selected", { providerSlug: selectedInsuranceProviderSlug ?? "unknown" });
  }, [selectedInsuranceProviderSlug]);

  const { buildIntakePhiFileInput } = useBuildIntakePhiFileInputFn();
  const { mutateAsync: createPhiFileReference } = useCreatePhiFileReferenceMut();

  const isBcbs = selectedInsuranceProviderSlug?.includes(BCBS_PROVIDER_LABEL_PREFIX) ?? false;
  const hasSpecifiedProvider = selectedInsuranceProviderSlug && selectedInsuranceProviderSlug !== "UNSURE_OTHER";
  const hasBothInsuranceImages = Boolean(frontInsuranceImageFile && backInsuranceImageFile);

  const choices = useMemo<SelectInputChoice[]>(() => {
    return [
      ...choicesFromApi,
      // extra choices - see the submit mutation to see where the non-ID choices
      // are parsed to determine whether to set to null (via `doesStringLookLikeDjangoGqlId()`)
      { label: t("intakeInsuranceInfo.unsureOther"), value: "UNSURE_OTHER", slug: "UNSURE_OTHER" },
    ];
  }, [choicesFromApi, t]);

  const uploadToS3AndReturnPhiFileRef = useCallback(
    async (file: File, slug: string) => {
      const { identifyingInfo, s3Key } = buildIntakePhiFileInput(slug);

      const mutationResp = await createPhiFileReference({
        identifyingInfo,
        s3Key,
        // NOTE: content-type nees to be set so the backend can use it generate
        // the S3 presigned url. Without that, S3 will 403 due to invalid signiture
        // match (S3 includes file's content-type in the signing algo)
        contentType: file.type,
      });

      const phiFileRef = mutationResp.phiFileReferenceCreate?.phiFileReference;

      const url = mutationResp.phiFileReferenceCreate?.s3UploadInfo?.uploadUrl;
      if (!url || !phiFileRef) {
        return;
      }

      // actual S3 upload
      await fetch(url, {
        method: "PUT",
        body: file,
        headers: {
          "Content-Type": file.type,
          "X-Amz-Meta-Identity": identifyingInfo,
        },
      });

      return phiFileRef;
    },
    [createPhiFileReference, buildIntakePhiFileInput],
  );

  const onSubmit = useCallback(
    async (data: TInsuranceInfoSchema) => {
      console.log("data", data);

      if (isBcbs && !hasBothInsuranceImages) {
        return;
      }

      let frontImageResp: PhiFileReferenceClaimType | undefined;
      if (frontInsuranceImageFile) {
        frontImageResp = await uploadToS3AndReturnPhiFileRef(frontInsuranceImageFile, "ins-card-front");
      }

      let backImageResp: PhiFileReferenceClaimType | undefined;
      if (backInsuranceImageFile) {
        backImageResp = await uploadToS3AndReturnPhiFileRef(backInsuranceImageFile, "ins-card-back");
      }

      handleInsuranceInfoSubmit(data, frontImageResp, backImageResp);
      afterSubmit();
    },
    [
      handleInsuranceInfoSubmit,
      afterSubmit,
      uploadToS3AndReturnPhiFileRef,
      frontInsuranceImageFile,
      backInsuranceImageFile,
      //
      isBcbs,
      hasBothInsuranceImages,
    ],
  );

  return (
    <IntakeFormScreenContainer title={t("intakeInsuranceInfo.header")}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="mb-4 mt-4 flex flex-col">
          <SelectInput
            {...register("insuranceProviderId")}
            label={t("intakeInsuranceInfo.providerName")}
            isRequired
            choices={choices}
            hookFormFieldError={errors.insuranceProviderId}
            onChange={(event) => {
              const selectedChoice = choices.find((choice) => choice.value === event.target.value);
              setSelectedInsuranceProviderSlug(selectedChoice?.slug ?? null);
              setValue("insuranceProviderSlug", selectedChoice?.slug);
            }}
          />
        </div>

        {/* Register the insurance provider's label so Yup schema can use it for conditional validation */}
        {/* This field is hidden but needed to keep the label in form state for validation logic */}
        <input type="hidden" {...register("insuranceProviderSlug")} />

        {hasSpecifiedProvider && (
          <>
            <div className="mb-4 mt-4 flex flex-col">
              <TextInput
                {...register("memberId")}
                isRequired
                label={t("intakeInsuranceInfo.memberId")}
                hookFormFieldError={errors.memberId}
              />
            </div>

            <div className="mb-4 mt-4 flex flex-col">
              <Typography>{t("intakeInsuranceInfo.instructions")}</Typography>
              <img src={insuranceCardImg} className="mt-2 sm:w-[75%]" />
            </div>
          </>
        )}

        {/* BCBS SPECIFIC UI */}
        {isBcbs && (
          <BCBSIntakeFormInsuranceInfo
            register={register}
            t={t}
            setValue={setValue}
            errors={errors}
            setIsChildPolicyHolder={setIsChildPolicyHolder}
            isChildPolicyHolder={isChildPolicyHolder}
            setFrontInsuranceImageFile={setFrontInsuranceImageFile}
            setBackInsuranceImageFile={setBackInsuranceImageFile}
            frontInsuranceImageFile={frontInsuranceImageFile}
            backInsuranceImageFile={backInsuranceImageFile}
          />
        )}

        <NextButton isDisabledOverride={isBcbs ? !hasBothInsuranceImages : false} />
      </form>
    </IntakeFormScreenContainer>
  );
});

IntakeFormInsuranceInfoScreen.displayName = "IntakeFormInsuranceInfoScreen";
